import { apiAgent } from '../../../axiosConfig.js'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { useSelector } from 'react-redux'
import ConditionalRender from '../../../ConditionalRender.js'
import Pagination from '../../../Pagination.js'
import reducer from "../../../components/utils/CommonReducer.js"
import { IconButton } from '../../../design_system/components/Button/Button.jsx'
import { Loader } from '../../../design_system/components/Loader.jsx'
import { Avatar } from '../../../design_system/components/Avatar/Avatar.jsx'
import { SearchBox } from '../../../design_system/components/Input Fields/SearchBox.jsx'
import { SelectBox } from '../../../design_system/components/Input Fields/SelectBox.jsx'
import { DropdownTrigger } from '../../../design_system/components/Dropdown/DropdownTrigger.jsx'
import { ClickableDropdown } from '../../../design_system/components/Dropdown/Dropdown.jsx'
import { DropdownMenu } from '../../../design_system/components/Dropdown/DropdownMenu.jsx'
import { DropdownItem } from '../../../design_system/components/Dropdown/DropdownItem.jsx'
import { Tooltip } from '../../../design_system/components/ToolTip/ToolTip.jsx'
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx'
import { AnConfirmPopup } from '../../cta_utils/AnConfirmPopup.js'
import MemberAccessPopup from './access/MemberAccessPopup.jsx'
import { formatMemberLastActive, memberRoleValue, reconcilePermissionOverrides } from './access/memberAccess.js'
import { canUseMemberManagementAction, delegatedMemberRoleOptions, isCustomerTeamAdmin, usesPolicyAuthorization } from '../../../utils/access.js'

const system_member_types = [{ label: "All", value: "all" }, { label: "Admin", value: "admin" }, { label: "Regular", value: "regular" }, { label: "Passive", value: "passive" }];
const role_options = [{ label: "Admin", value: "admin" }, { label: "Regular", value: "regular" }];
const ActiveTeamMembers = ({ inviteEmail, onCountChange, accessData }) => {

  const [componentData, setComponentData] = useReducer(reducer, {
    activeMembers: [],
    currentPage: 1,
    searchTerm: "",
    memberType: system_member_types[0],
    totalPages: 1,
    isLoading: {}
  })

  let { activeMembers, currentPage, searchTerm, memberType,
    totalPages, isLoading } = componentData;
  const [searchInput, setSearchInput] = useState("");
  const toast = useToast();

  const setState = (payload) => {
    setComponentData({ type: "SET_STATE", payload })
  }

  const current_user = useSelector(state => state.current_user.user)
  const current_team = useSelector(state => state.current_team.team)

  const [popupsList, setPopupsList] = useState({})
  const [idx, setIdx] = useState();
  const [pendingRole, setPendingRole] = useState({ memberIndex: null, role: null, accessRoleId: null, roleLabel: null });
  const [isRemoving, setIsRemoving] = useState(false);
  const [isResending, setIsResending] = useState(null);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [accessEditor, setAccessEditor] = useState(null);

  const fetchMemberDetails = useCallback((query = null, currentPage) => {
    setState({ isLoading: { fetchingMembers: true } })
    apiAgent({
      method: 'get',
      url: '/team_members',
      params: {
        approval_status: 'accepted',
        name_query: query,
        role: memberType?.value === "all" || memberType?.accessRoleId ? "" : memberType?.value,
        access_role_id: memberType?.accessRoleId || "",
        page: currentPage
      }
    }).then(res => {
      if (res.data) {
        let result_data = res.data.data.map(member => ({ ...member }))
        setState({ activeMembers: result_data, totalPages: res.data.total_pages, isLoading: {} })
        onCountChange?.(res.data.total_count || 0)
      }
    }).catch(err => {
      console.log("Error from ActiveTeamMembers component did mount", err)
      setState({ isLoading: {} })
    })
  }, [memberType, onCountChange])

  useEffect(() => {
    fetchMemberDetails(searchTerm, currentPage)
  }, [inviteEmail, fetchMemberDetails, searchTerm, currentPage])

  const handleOnKeyDown = (e) => {
    if (e.key === 'Enter') {
      setState({ searchTerm: searchInput, currentPage: 1 })
    }
  }

  const removeTeamMember = (index = idx) => {
    const team_member = activeMembers[index]
    const wasInvited = team_member.status === 'invited'
    setIsRemoving(true);
    apiAgent({
      method: 'post',
      url: `/team_members/${team_member.id}/remove_member`,
      data: {}
    }).then(res => {
      let temp = [...activeMembers]
      temp.splice(index, 1)
      inviteEmail = true
      setState({ activeMembers: [...temp] });
      if (accessEditor && activeMembers[accessEditor.memberIndex]?.id === team_member.id) setAccessEditor(null);
      toast.warning(wasInvited ? "Invite revoked" : "Team member removed");
      setPopupsList({});
      setIsRemoving(false);
    }).catch(err => {
      toast.error('Something went wrong! Unable to remove team member');
      setIsRemoving(false);
    })
  }

  const resendInvite = (index) => {
    const team_member = activeMembers[index]
    setIsResending(index);
    apiAgent({
      method: 'post',
      url: `/team_members/${team_member.id}/resend_invite`,
      data: {}
    }).then(res => {
      toast.success(`Invite resent to ${team_member.user.email}`)
      setIsResending(null);
    }).catch(err => {
      toast.error(err?.response?.data?.error_message || 'Failed to resend invite')
      setIsResending(null);
    })
  }

  const copyInviteLink = (index) => {
    const team_member = activeMembers[index]
    const link = `${window.location.origin}/join_invite?invitation=${team_member.user.id}&team_id=${team_member.team.id}`
    navigator.clipboard.writeText(link).then(() => {
      toast.success('Invite link copied to clipboard')
    }).catch(() => {
      toast.error('Unable to copy invite link')
    })
  }


  const decodeRoleSelection = (roleValue) => {
    if (roleValue.startsWith("custom:")) {
      const accessRoleId = roleValue.slice("custom:".length);
      const customRole = (accessData?.custom_roles || []).find(role => role.id === accessRoleId) ||
        (current_team.access_role?.id === accessRoleId ? current_team.access_role : null);
      return { role: "custom", accessRoleId, roleLabel: customRole?.name || "Custom" };
    }
    return { role: roleValue, accessRoleId: null, roleLabel: roleValue };
  }

  const handleRoleChange = (roleValue, memberIndex) => {
    if (roleValue === memberRoleValue(activeMembers[memberIndex])) return;
    setPendingRole({ memberIndex, ...decodeRoleSelection(roleValue) });
    setPopupsList({ roleChangePopup: true });
  }

  const confirmRoleChange = () => {
    const { memberIndex, role, accessRoleId } = pendingRole;
    setIsChangingRole(true);
    const roleChangeData = {
      id: activeMembers[memberIndex].id,
      role,
      access_role_id: accessRoleId,
      access_lock_version: activeMembers[memberIndex].access_lock_version
    };
    apiAgent({
      method: 'post',
      url: '/team_members/update_role',
      data: roleChangeData
    }).then(res => {
      if (activeMembers[memberIndex].user.id === current_user.id) window.location.reload();
      let temp = [...activeMembers]
      temp[memberIndex].team_role = role;
      temp[memberIndex].access_role = role === "custom"
        ? (accessData?.custom_roles || []).find(item => item.id === accessRoleId) ||
          (current_team.access_role?.id === accessRoleId ? current_team.access_role : null)
        : null;
      temp[memberIndex].access_lock_version = res.data?.member?.access_lock_version ??
        temp[memberIndex].access_lock_version;
      setState({ activeMembers: [...temp] });
      toast.success(`Access rights updated for ${temp[memberIndex].user.name}`);
      setPopupsList({});
      setIsChangingRole(false);
    }).catch(err => {
      toast.error(err?.response?.data?.error || err?.response?.data?.error_message || 'Something went wrong! Unable to update role');
      if (err?.response?.status === 409) {
        setPopupsList({});
        setPendingRole({ memberIndex: null, role: null, accessRoleId: null, roleLabel: null });
        fetchMemberDetails(searchTerm, currentPage);
      }
      setIsChangingRole(false);
    })
  }

  const confirmPopup = () => {
    const target = activeMembers[idx]
    const isInvited = target?.status === 'invited'
    return (
      <AnConfirmPopup
        maxWidth={424}
        name={isInvited ? "Revoke invite?" : "Delete User?"}
        confirm_message={isInvited
          ? "This will revoke the pending invite. The user will no longer be able to join the team using the invite link."
          : "This will permanently remove the user account and all associated data. They will lose access immediately and this action cannot be undone."}
        btnLabels={{ submitLabel: "Confirm", cancelLabel: "Cancel" }}
        isLoading={isRemoving}
        onSubmitCallback={() => removeTeamMember()}
        onCancelCallback={() => setPopupsList({})}
      />)
  }

  const policyAccessEnabled = usesPolicyAuthorization(current_team);
  const customerTeamAdmin = isCustomerTeamAdmin({ team: current_team });
  const canChangeMemberRoles = customerTeamAdmin;
  const canRemoveMembers = canUseMemberManagementAction({ permissionKey: "members.remove", user: current_user, team: current_team });
  const canInviteMembers = canUseMemberManagementAction({ permissionKey: "members.invite", user: current_user, team: current_team });

  const customRoleOptions = (accessData?.custom_roles || []).map(role => ({
    label: role.name,
    value: "custom:" + role.id
  }));
  const policyRoleOptions = [
    { label: "Admin", value: "admin" },
    { label: "Regular", value: "regular" },
    ...customRoleOptions
  ];
  const delegatedRoleOptions = delegatedMemberRoleOptions({ team: current_team, customRoles: accessData?.custom_roles });
  const memberTypeOptions = [
    ...system_member_types,
    ...(accessData?.custom_roles || []).map(role => ({
      label: role.name,
      value: "custom:" + role.id,
      accessRoleId: role.id
    }))
  ];

  const canEditTargetRole = (member) => {
    if (!canChangeMemberRoles) return false;
    if (member.team_role === "passive") return false;
    return true;
  }

  const canRemoveTarget = (member) =>
    canRemoveMembers && (customerTeamAdmin || member.team_role === "custom");

  const canRevokeInviteTarget = (member) =>
    canInviteMembers && (customerTeamAdmin ||
      delegatedRoleOptions.some(option => option.value === memberRoleValue(member)));

  const canResendInviteTarget = (member) =>
    canInviteMembers && (customerTeamAdmin ||
      delegatedRoleOptions.some(option => option.value === memberRoleValue(member)));

  const renderRoleDropdown = (member, index) => {
    const currentValue = policyAccessEnabled ? memberRoleValue(member) : member.team_role;
    const options = policyAccessEnabled
      ? policyRoleOptions
      : role_options;

    return (
      <SelectBox
        value={currentValue}
        placeholder={member.team_role === "custom" ? member.access_role?.name || "Custom" : member.team_role}
        options={options}
        handleOnChange={(val) => {
          if (val === currentValue) return;
          if (policyAccessEnabled && customerTeamAdmin) {
            setAccessEditor({ memberIndex: index, initialRoleValue: val });
          } else {
            handleRoleChange(val, index);
          }
        }}
        containerClass={policyAccessEnabled ? 'manage-teams__role-select' : undefined}
        triggerStyle={policyAccessEnabled ? undefined : { border: '1px solid var(--color-border-subtle)' }}
        variant={policyAccessEnabled ? 'default' : 'ghost'}
        Size="Large"
      />
    )
  }

  const handleAccessSaved = (updatedMember, reloadCurrentUser) => {
    const memberIndex = accessEditor.memberIndex;
    const nextMembers = [...activeMembers];
    nextMembers[memberIndex] = {
      ...nextMembers[memberIndex],
      team_role: updatedMember.role,
      access_role: updatedMember.access_role,
      permission_overrides: updatedMember.permission_overrides,
      effective_permissions: updatedMember.effective_permissions,
      access_lock_version: updatedMember.access_lock_version
    };
    setState({ activeMembers: nextMembers });
    setAccessEditor(null);
    if (reloadCurrentUser) window.location.reload();
  }

  const openMemberAccess = (memberIndex) => {
    setAccessEditor({ memberIndex, initialRoleValue: memberRoleValue(activeMembers[memberIndex]) });
  }

  const memberExceptionCount = (member) => {
    if (member.team_role !== "custom") return 0;

    const assignedRole = (accessData?.custom_roles || [])
      .find(role => role.id === member.access_role?.id);
    return Object.keys(reconcilePermissionOverrides(
      member.permission_overrides,
      assignedRole?.permission_keys
    )).length;
  }

  const renderMemberExceptionCount = (member, memberIndex) => {
    const exceptionCount = memberExceptionCount(member);
    if (!customerTeamAdmin || exceptionCount === 0) return null;

    const exceptionLabel = `${exceptionCount} exception${exceptionCount === 1 ? "" : "s"}`;
    const exceptionTooltip = `${exceptionCount} permission exception${exceptionCount === 1 ? "" : "s"}`;
    return (
      <Tooltip tooltipText={exceptionTooltip} placement="top" showWithoutOverflowing>
        <button
          type="button"
          className="manage-teams__exception-count support-m"
          aria-label={exceptionLabel}
          onClick={() => openMemberAccess(memberIndex)}
        >
          {exceptionCount}
        </button>
      </Tooltip>
    );
  }

  const renderMemberActions = (member, index) => {
    const isCurrentUser = member.user.id === current_user.id;

    if (member.status === 'invited') {
      if (isCurrentUser) return null;
      if (!canResendInviteTarget(member) && !canRevokeInviteTarget(member)) return null;
      const inviteActions = (
        <ClickableDropdown>
          <DropdownTrigger ShowHover={false} IsIconVisible={false} customStyle={{ padding: 0, cursor: 'pointer' }}>
            <IconButton Size="ExtraSmall" Type="Tertiary" IconName="vertical-ellipsis" ariaLabel="Invite actions" />
          </DropdownTrigger>
          <DropdownMenu usePortal align="right">
            {canResendInviteTarget(member) && <DropdownItem IconName="redo" onClick={() => resendInvite(index)} disabled={isResending === index}>{isResending === index ? "Resending..." : "Resend invite"}</DropdownItem>}
            {canResendInviteTarget(member) && <DropdownItem IconName="copy-link" onClick={() => copyInviteLink(index)}>Copy invite link</DropdownItem>}
            {canRevokeInviteTarget(member) && <DropdownItem IconName="delete" onClick={() => { setIdx(index); setPopupsList({ confirmPopup: true }) }} customClasses="dropdown-item-danger">Revoke invite</DropdownItem>}
          </DropdownMenu>
        </ClickableDropdown>
      );
      return policyAccessEnabled ? (
        <div className="manage-teams__row-actions">
          {inviteActions}
          <span className="manage-teams__action-slot" aria-hidden="true" />
        </div>
      ) : inviteActions;
    }

    if (!policyAccessEnabled) {
      if (isCurrentUser || !canRemoveTarget(member)) return null;
      return (
        <IconButton
          Size="ExtraSmall"
          Type="DangerTertiary"
          IconName="delete"
          ariaLabel="Remove member"
          onClick={() => { setIdx(index); setPopupsList({ confirmPopup: true }) }}
        />
      );
    }

    const canManageAccess = canEditTargetRole(member);
    return (
      <div className="manage-teams__row-actions">
        {!isCurrentUser && canRemoveTarget(member) && (
          <IconButton Size="ExtraSmall" Type="DangerTertiary" IconName="delete" ariaLabel="Remove member"
            onClick={() => { setIdx(index); setPopupsList({ confirmPopup: true }) }} />
        )}
        {canManageAccess ? (
          <IconButton Size="ExtraSmall" Type="Tertiary" IconName="chevron-right"
            ariaLabel={"Manage access for " + (member.user.name || member.user.email)}
            onClick={() => openMemberAccess(index)} />
        ) : member.team_role === 'passive' && (
          <span className="manage-teams__action-slot" aria-hidden="true" />
        )}
      </div>
    );
  }

  const renderLegacyRole = (member, index) => (
    <>
      <ConditionalRender if={customerTeamAdmin && member.team_role !== "passive"}>
        <ConditionalRender if={!current_team.is_scim_enabled}>
          {renderRoleDropdown(member, index)}
        </ConditionalRender>
        <ConditionalRender if={current_team.is_scim_enabled}>
          <span className="body-m capitalize">{member.team_role}</span>
        </ConditionalRender>
      </ConditionalRender>
      <ConditionalRender if={!customerTeamAdmin || member.team_role === "passive"}>
        <span className="body-m capitalize">{member.team_role}</span>
      </ConditionalRender>
    </>
  );
  return (
    <>
      <div className="manage-teams__search-row">
        <SearchBox
          value={searchInput}
          handleOnChange={(e) => setSearchInput(e.target.value)}
          handleOnKeyDown={handleOnKeyDown}
          placeholder="Search by name or email"
          customStyle={{ width: 320 }}
        />
        <div className="manage-teams__filter">
          <span className="body-m color-text-primary">Filter by</span>
          <SelectBox
            value={memberType.value}
            options={memberTypeOptions}
            handleOnChange={(val) => setState({ memberType: memberTypeOptions.find(o => o.value === val), currentPage: 1 })}
            triggerStyle={{ border: '1px solid var(--color-border-subtle)' }}
            variant="ghost"
            Size="Large"
            align="right"
          />
        </div>
      </div>
      <ConditionalRender if={isLoading.fetchingMembers}>
        <div className="flex justify-center items-center" style={{ height: "10vh" }}><Loader isLoading={true} /></div>
      </ConditionalRender>
      <ConditionalRender if={!isLoading.fetchingMembers}>
        <Pagination currentPage={currentPage} onPageChange={(v) => setState({ currentPage: v })} totalPages={totalPages} style={{ padding: 0, marginTop: "var(--space-l)" }} />
        <div className={`data-fields__table-wrap manage-teams__table-wrap ${policyAccessEnabled ? 'manage-teams__table-wrap--policy' : ''}`}>
          <table className={`data-fields__table manage-teams__table ${policyAccessEnabled ? "manage-teams__table--policy" : ""} ${current_team.is_scim_enabled ? "manage-teams__table--no-status" : ""}`}>
            <thead>
              <tr>
                <th className="body-m-bold">Name</th>
                <th className="body-m-bold">Role</th>
                {!current_team.is_scim_enabled && <th className="body-m-bold">Status</th>}
                <th className="body-m-bold">Last Active</th>
                {!current_team.is_scim_enabled && <th></th>}
              </tr>
            </thead>
            <tbody>
              {
                activeMembers.map((member, index) => (
                  <ConditionalRender key={member.id} if={current_user.is_publisher || !member.user.is_super_member}>
                    <tr
                      className={policyAccessEnabled && canEditTargetRole(member) && member.status !== 'invited' ? "manage-teams__member-row--access" : ""}
                      onClick={(event) => {
                        if (!policyAccessEnabled || !canEditTargetRole(member) || member.status === 'invited') return;
                        if (event.target.closest("button, a, input, [role='button'], .dropdown-root, .kb-dropdown-menu")) return;
                        openMemberAccess(index);
                      }}
                    >
                      <td>
                        <div className="manage-teams__name-cell">
                          <Avatar name={member.user.name || member.user.email} width={policyAccessEnabled ? 36 : 40} />
                          <div className="manage-teams__member-name">
                            <div className="body-s-bold">{member.user.name}</div>
                            <ConditionalRender if={member.team_role !== "passive"}>
                              <div className="body-s color-text-secondary" title={member.user.email}>{member.user.email}</div>
                            </ConditionalRender>
                          </div>
                        </div>
                      </td>
                      <td>
                        {!policyAccessEnabled ? renderLegacyRole(member, index) : (
                          <div className="manage-teams__role-cell">
                            <ConditionalRender if={canEditTargetRole(member)}>
                              <ConditionalRender if={!current_team.is_scim_enabled}>
                                {renderRoleDropdown(member, index)}
                              </ConditionalRender>
                              <ConditionalRender if={current_team.is_scim_enabled}>
                                <span className="body-m capitalize">{member.team_role}</span>
                              </ConditionalRender>
                            </ConditionalRender>
                            <ConditionalRender if={!canEditTargetRole(member)}>
                              <span className="body-m capitalize">
                                {member.team_role === "custom" ? member.access_role?.name || "Custom" : member.team_role}
                              </span>
                            </ConditionalRender>
                            {renderMemberExceptionCount(member, index)}
                          </div>
                        )}
                      </td>
                      {!current_team.is_scim_enabled && (
                        <td>
                          <span className={`manage-teams__badge manage-teams__badge--${member.status === 'invited' ? 'invited' : 'active'} body-s-bold`}>
                            {member.status === 'invited' ? 'Invited' : 'Active'}
                          </span>
                        </td>
                      )}
                      <td>
                        <span className="body-m-bold color-text-secondary">
                          {formatMemberLastActive(member.last_active_at)}
                        </span>
                      </td>
                      <ConditionalRender if={!current_team.is_scim_enabled}>
                        <td
                          className={policyAccessEnabled ? "manage-teams__actions-cell" : undefined}
                          style={!policyAccessEnabled ? { textAlign: 'center', width: 48 } : undefined}
                        >
                          {renderMemberActions(member, index)}
                        </td>
                      </ConditionalRender>
                    </tr>
                  </ConditionalRender>
                ))
              }
            </tbody>
          </table>
        </div>
      </ConditionalRender>
      {popupsList.confirmPopup && confirmPopup()}
      {accessEditor && activeMembers[accessEditor.memberIndex] && (
        <MemberAccessPopup
          member={activeMembers[accessEditor.memberIndex]}
          accessData={accessData}
          initialRoleValue={accessEditor.initialRoleValue}
          currentUserId={current_user.id}
          onClose={() => setAccessEditor(null)}
          onSaved={handleAccessSaved}
          onConflict={() => {
            setAccessEditor(null);
            fetchMemberDetails(searchTerm, currentPage);
          }}
          onRemove={canRemoveTarget(activeMembers[accessEditor.memberIndex]) &&
            activeMembers[accessEditor.memberIndex].user.id !== current_user.id
            ? () => { setIdx(accessEditor.memberIndex); setPopupsList({ confirmPopup: true }); }
            : undefined}
        />
      )}
      {popupsList.roleChangePopup && pendingRole.memberIndex !== null && (
        <AnConfirmPopup
          maxWidth={424}
          name="Change user role?"
          confirm_message={
            <>
              Changing user role from <span className="body-m-bold color-text-primary capitalize">{activeMembers[pendingRole.memberIndex]?.team_role}</span> to <span className="body-m-bold color-text-primary capitalize">{pendingRole.roleLabel}</span>. This will update their permissions and access level across the portal immediately.
            </>
          }
          btnLabels={{ submitLabel: "Confirm", cancelLabel: "Cancel" }}
          isLoading={isChangingRole}
          onSubmitCallback={confirmRoleChange}
          onCancelCallback={() => setPopupsList({})}
        />
      )}
    </>
  )
}

export default ActiveTeamMembers
